import { skipToken } from "@reduxjs/toolkit/query";

import { useAppSelector } from "~/app/store";

import { useDebounce } from "~/hooks/useDebounce";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetPutawayTasksWithPagingQuery } from "~/redux/warehouse/putAwayTasks.hooks";
import { PutAwayTaskSummaryDto, TemperatureZone } from "~/types/api";

import { useInductionConfiguration } from "./useInductionConfiguration";

export type SearchData = {
  searchInputValue?: string;
  scannedBarcodes: string[];
  offset: number;
};

const emptyArray: PutAwayTaskSummaryDto[] = [];

export const useGetPutawayTasks = (
  pageLimit: number,
  searchData: SearchData
) => {
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const debouncedSearchData = useDebounce(searchData, 200, searchData);

  const { isMultiTempGrid, tempZones } = useGetMultiTempGridInfo();
  const { tempZone, isLoading: isLoadingInductionConfiguration } =
    useInductionConfiguration();

  const getTemperatureZones = (): TemperatureZone[] | null => {
    if (
      siteWorkstation &&
      (isLoadingInductionConfiguration || tempZones.length === 0)
    ) {
      return null;
    }

    return isMultiTempGrid && tempZone ? [tempZone] : tempZones;
  };

  const temperatureZones = getTemperatureZones();
  const shouldSkip =
    isLoadingInductionConfiguration || temperatureZones === null;

  const query = useGetPutawayTasksWithPagingQuery(
    shouldSkip
      ? skipToken
      : {
          status: ["scheduled"],
          search: debouncedSearchData.searchInputValue,
          searches: debouncedSearchData.scannedBarcodes,
          binType: ["induction", "receiving"],
          limit: pageLimit,
          offset: (debouncedSearchData.offset - 1) * pageLimit,
          temperatureZone: temperatureZones,
          searchSource: debouncedSearchData.scannedBarcodes.length
            ? "scanner"
            : undefined,
          workstationId: siteWorkstation?.id
        },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({
        data,
        isLoading,
        isFetching,
        error,
        originalArgs
      }) => ({
        putawayTasks: data?.putAwayTasks ?? emptyArray,
        putawayTasksLoading: isLoading || shouldSkip,
        putawayTasksFetching: isFetching || shouldSkip,
        putawayTasksTotalCount: data?.putAwayTasksCount ?? 0,
        error,
        originalArgs
      })
    }
  );

  return query;
};
